import React, { useState, useRef, useEffect } from 'react';
import { API_URL } from '../config';

const AIAssistant = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isConfigured, setIsConfigured] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);

  // Check for speech recognition support
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      setSpeechSupported(true);
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');
        setInputValue(transcript);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };
    }
  }, []);

  // Check if assistant is configured
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${API_URL}/assistant/status`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const data = await response.json();
        setIsConfigured(data.configured);
      } catch (error) {
        console.error('Error checking assistant status:', error);
        setIsConfigured(false);
      }
    };
    if (isOpen) {
      checkStatus();
    }
  }, [isOpen]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const sendMessage = async (messageText, confirmAction = false) => {
    if (!messageText.trim() && !confirmAction) return;

    const userMessage = messageText.trim();

    if (!confirmAction) {
      setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
      setInputValue('');
    }

    setIsLoading(true);

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/assistant/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: userMessage,
          history: messages,
          pending_action: pendingAction,
          confirm_action: confirmAction
        })
      });

      const data = await response.json();

      if (data.error) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `Error: ${data.error}`,
          isError: true
        }]);
      } else {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.response
        }]);

        if (data.requires_confirmation) {
          setPendingAction(data.pending_action);
        } else {
          setPendingAction(null);
        }
      }
    } catch (error) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Connection error: ${error.message}`,
        isError: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputValue);
    }
  };

  const handleConfirm = () => {
    sendMessage('Yes, proceed', true);
  };

  const handleCancel = () => {
    setPendingAction(null);
    setMessages(prev => [...prev, {
      role: 'assistant',
      content: 'Action cancelled.'
    }]);
  };

  const toggleListening = () => {
    if (!recognitionRef.current) return;

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      setInputValue('');
      recognitionRef.current.start();
      setIsListening(true);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      bottom: '80px',
      right: '20px',
      width: '380px',
      height: '500px',
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
      display: 'flex',
      flexDirection: 'column',
      zIndex: 1000,
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        padding: '16px',
        backgroundColor: '#1a73e8',
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '20px' }}>🤖</span>
          <span style={{ fontWeight: '600' }}>SurveyPro Assistant</span>
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            color: 'white',
            fontSize: '24px',
            cursor: 'pointer',
            padding: '0',
            lineHeight: '1'
          }}
        >
          ×
        </button>
      </div>

      {/* Messages */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
      }}>
        {isConfigured === false && (
          <div style={{
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '8px',
            padding: '12px',
            fontSize: '14px'
          }}>
            <strong>Assistant not configured</strong>
            <p style={{ margin: '8px 0 0 0' }}>
              The AI assistant requires an API key to be set on the server.
              Contact your administrator to set up the ANTHROPIC_API_KEY or OPENAI_API_KEY.
            </p>
          </div>
        )}

        {messages.length === 0 && isConfigured !== false && (
          <div style={{
            textAlign: 'center',
            color: '#666',
            marginTop: '40px'
          }}>
            <div style={{ fontSize: '40px', marginBottom: '16px' }}>👋</div>
            <p style={{ margin: '0 0 8px 0', fontWeight: '500' }}>Hi! I'm your SurveyPro assistant.</p>
            <p style={{ margin: '0', fontSize: '14px', color: '#888' }}>
              Ask me about jobs, time entries, invoices, or team hours.
            </p>
            <div style={{ marginTop: '20px', fontSize: '13px', color: '#888' }}>
              <p style={{ margin: '4px 0' }}>Try asking:</p>
              <p style={{ margin: '4px 0', fontStyle: 'italic' }}>"How many hours did Ryan work this week?"</p>
              <p style={{ margin: '4px 0', fontStyle: 'italic' }}>"What jobs did we complete this week?"</p>
              <p style={{ margin: '4px 0', fontStyle: 'italic' }}>"Show me unpaid invoices"</p>
            </div>
          </div>
        )}

        {messages.map((msg, index) => (
          <div
            key={index}
            style={{
              alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
              maxWidth: '85%'
            }}
          >
            <div style={{
              backgroundColor: msg.role === 'user' ? '#1a73e8' : (msg.isError ? '#fee2e2' : '#f1f3f4'),
              color: msg.role === 'user' ? 'white' : (msg.isError ? '#dc2626' : '#333'),
              padding: '10px 14px',
              borderRadius: msg.role === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
              fontSize: '14px',
              lineHeight: '1.4',
              whiteSpace: 'pre-wrap'
            }}>
              {msg.content}
            </div>
          </div>
        ))}

        {isLoading && (
          <div style={{ alignSelf: 'flex-start' }}>
            <div style={{
              backgroundColor: '#f1f3f4',
              padding: '10px 14px',
              borderRadius: '18px 18px 18px 4px',
              display: 'flex',
              gap: '4px'
            }}>
              <span className="typing-dot" style={{ animationDelay: '0ms' }}>●</span>
              <span className="typing-dot" style={{ animationDelay: '150ms' }}>●</span>
              <span className="typing-dot" style={{ animationDelay: '300ms' }}>●</span>
            </div>
          </div>
        )}

        {pendingAction && (
          <div style={{
            backgroundColor: '#e8f4fd',
            border: '1px solid #1a73e8',
            borderRadius: '8px',
            padding: '12px',
            display: 'flex',
            gap: '8px',
            justifyContent: 'flex-end'
          }}>
            <button
              onClick={handleCancel}
              style={{
                padding: '8px 16px',
                border: '1px solid #ccc',
                borderRadius: '6px',
                backgroundColor: 'white',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: '6px',
                backgroundColor: '#1a73e8',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Confirm
            </button>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div style={{
        padding: '12px 16px',
        borderTop: '1px solid #e0e0e0',
        display: 'flex',
        gap: '8px',
        alignItems: 'center'
      }}>
        {speechSupported && (
          <button
            onClick={toggleListening}
            disabled={isLoading || isConfigured === false}
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              border: 'none',
              backgroundColor: isListening ? '#dc3545' : '#f1f3f4',
              color: isListening ? 'white' : '#666',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              transition: 'background-color 0.2s',
              flexShrink: 0
            }}
            title={isListening ? 'Stop listening' : 'Voice input'}
          >
            🎤
          </button>
        )}
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={isListening ? 'Listening...' : 'Ask me anything...'}
          disabled={isLoading || isConfigured === false}
          style={{
            flex: 1,
            padding: '10px 14px',
            border: isListening ? '2px solid #dc3545' : '1px solid #e0e0e0',
            borderRadius: '20px',
            fontSize: '14px',
            outline: 'none',
            backgroundColor: isListening ? '#fff5f5' : 'white'
          }}
        />
        <button
          onClick={() => sendMessage(inputValue)}
          disabled={isLoading || !inputValue.trim() || isConfigured === false}
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            border: 'none',
            backgroundColor: isLoading || !inputValue.trim() ? '#e0e0e0' : '#1a73e8',
            color: 'white',
            cursor: isLoading || !inputValue.trim() ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '18px',
            flexShrink: 0
          }}
        >
          ➤
        </button>
      </div>

      <style>
        {`
          @keyframes typing {
            0%, 60%, 100% { opacity: 0.3; }
            30% { opacity: 1; }
          }
          .typing-dot {
            color: #666;
            font-size: 8px;
            animation: typing 1s infinite;
          }
        `}
      </style>
    </div>
  );
};

export default AIAssistant;
